import React, { useState, useCallback, useEffect, useRef } from "react"
import { connect } from "react-redux"
import cn from "classnames"
import { useTranslation } from "react-i18next"
import storageLocal from "@/common/storage"
import Slider from "@/common/components/slider"
import Scrollable from "@/common/components/scrollable"
import type { State } from "@/signomatic/store/reducers"
import { actions } from "@/signomatic/store/actions"
import Toolbox from "@/signomatic/components/toolbox"
import Sign from "@/signomatic/app/sign"
import Icons from "@/images/signomatic/objects"
import ImageArchive, { addImage, imageURL, rowImagesCount } from "@/signomatic/components/image_archive"
import Hint from "@/signomatic/components/hint"
import SignTypeLink from "@/signomatic/components/sign_type_link"
import type { ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import ImageSmallIcon from "@/skylticons/image_small"
import ImageLargeIcon from "@/skylticons/image"
import ObjectBorderPicker from "@/signomatic/components/object_border_picker"
import ColorPicker from "@/signomatic/components/color_picker"
import ShapeIcons from "@/images/signomatic/shapes"
import ColorUtil from "@/signomatic/util/colors"
import {
  ChevronLeftLarge,
  ChevronRightLarge,
  TrashOutline,
  DuplicateOutline,
  Reset,
  CloseSmall,
  ArchiveFill,
  ArrowRight,
} from "@/skylticons"
import LoadingAnimation from "@/common/components/loading"
import AsyncImage from "@/signomatic/components/async_image"
import { sizeUnit } from "@/common/locales"
import Calc from "@/common/util/calculator"
import type Uppy from "@uppy/core"
import { FileInput } from "@uppy/react"
import { useUppy } from "@/common/hooks/use_uppy"
import { useSign } from "@/signomatic/contexts"
import { useSignState } from "@/signomatic/app/state_bridge"
import ClientData from "@/common/client_data"
import type { FailedUppyFile, UploadedUppyFile, UppyFile } from "@uppy/core"
import { arrayUniq } from "@/signomatic/util"
import { graphql } from "@/api"
import { useMutation, useQuery } from "@apollo/client"
import type { UploadedImagesQuery, UploadedImagesQueryVariables } from "@/api/graphql"
import invariant from "tiny-invariant"

const imagesDoc = graphql(`
  query ImageUploader($color: Boolean!) {
    imageTags {
      images(color: $color) {
        id
        color
        description
        width
        maxWidth
        height
        maxHeight
        size
        sizeHuman
        filename
      }
    }
  }
`)

const uploadedImagesDoc = graphql(`
  query UploadedImages {
    uploadedImages {
      id
      description
      width
      maxWidth
      height
      maxHeight
      size
      sizeHuman
      filename
    }
  }
`)

const imageDeleteMutation = graphql(`
  mutation ImageDelete($id: Int!) {
    imageDelete(id: $id) {
      success
      error
      images {
        id
        description
        width
        maxWidth
        height
        maxHeight
        size
        sizeHuman
        filename
      }
    }
  }
`)

const imageDirectUploadMutation = graphql(`
  mutation ImageDirectUpload($filename: String!, $fileData: JSON!) {
    imageDirectUpload(filename: $filename, fileData: $fileData) {
      success
      error

      image {
        id
        description
        width
        maxWidth
        height
        maxHeight
        size
        sizeHuman
        filename
      }

      images {
        id
        description
        width
        maxWidth
        height
        maxHeight
        size
        sizeHuman
        filename
      }
    }
  }
`)

type Section = "all-images" | "recently-used-page"
type Tab = "all-objects" | "active-image" | "new-image" | "image-size" | "alignments" | "border" | "color"

const formatSize = (width: number, height: number) => {
  width = Calc.pxToMm(width)
  height = Calc.pxToMm(height)

  const us = sizeUnit === "in"
  if (us) {
    width = Calc.mmToIn(width)
    height = Calc.mmToIn(height)
  }

  return `${width.toFixed(us ? 1 : 0)}x${height.toFixed(us ? 1 : 0)} ${sizeUnit}`
}

const cacheDivider = "<SM&I>"

const cacheImage = (id: string) => {
  const oldArray = storageLocal.getItem("recentImages")
  storageLocal.setItem("recentImages", `${id}${oldArray ? `${cacheDivider}${oldArray}` : ""}`)
}

interface ButtonProps {
  name: string
  label?: string
  active?: boolean
  onClick: (e: React.FormEvent) => void
  classes?: string
}

const Button: React.FC<ButtonProps> = ({ name, active, label, onClick, classes }) => (
  <div className={cn(name, classes, { active })} onClick={onClick}>
    <div className="image">{Icons[name] ? React.createElement(Icons[name]) : ""}</div>
    {label && <div className="name paragraph-2xs center mb-0">{label}</div>}
  </div>
)

interface ImageIconProps {
  obj: SignImageState
  sign: Sign
  classes?: string
}

const ImageIcon: React.FC<ImageIconProps> = ({ obj, sign, classes }) => {
  const multicolor = sign.fullData.multicolor
  const color = multicolor && obj.colored ? "true" : "false"
  let url = `/image/get?id=${obj.id}&width=96&height=96&color=${color}`

  if (!multicolor || !obj.colored) {
    // display always black, no matter what color customer has chosen
    url += "&text_color=000000&background_color=ffffff"
  }

  return (
    <AsyncImage
      src={url}
      loadingElement={() => <LoadingAnimation />}
      classes={cn("w-12 h-12 mb-1 data-hj-suppress", classes)}
    />
  )
}

const uppyFileMap: Record<string, UploadedImagesQuery["uploadedImages"][0]> = {}
const allowedFileTypes = ClientData.allowedImageTypes?.split(",")

interface Image {
  __typename?: "Image" | undefined
  id: string
  description?: string | null
  color: boolean
  width: number
  height: number
  filename?: string | null
}

const RecentImages: React.FC<{
  recentImageIds: string[]
  uppy: Uppy
  usedArchiveImages: Image[]
  files: Array<UploadedUppyFile<any, any> | FailedUppyFile<any, any>>
  images: any[]
  handleAddImage: () => void
}> = ({ recentImageIds, uppy, usedArchiveImages, files, images, handleAddImage }) => {
  return (
    <>
      {recentImageIds.map(id => {
        const archiveImage = usedArchiveImages.find(used => used.id == id)
        const image = images.find(image => image.filename == id)
        const file = files.find(file => file.name == id)

        if (archiveImage) {
          return (
            <UploadedImage
              key={archiveImage.id}
              uppy={uppy}
              archiveImg={archiveImage}
              handleAddImage={handleAddImage}
            />
          )
        } else if (image) {
          return <UploadedImage key={image.id} uppy={uppy} img={image} file={file} handleAddImage={handleAddImage} />
        } else if (file) {
          return <UploadedImage key={file.id} uppy={uppy} file={file} handleAddImage={handleAddImage} />
        } else {
          return
        }
      })}
    </>
  )
}

interface AllObjectsTabProps {
  sign: Sign
  signState: SignState
  setTab: (tab: string) => void
  loadingPlaceholder: boolean
  setLoadingImage: (value: boolean) => void
  color: boolean
}

const AllObjectsTab: React.FC<AllObjectsTabProps> = ({
  sign,
  signState,
  setTab,
  loadingPlaceholder,
  setLoadingImage,
  color,
}) => {
  const { t } = useTranslation()
  const { data, loading, error } = useQuery(uploadedImagesDoc)
  const { uppy, state: uppyState, invalidFiles } = useUppy("signomatic-images", allowedFileTypes)
  const allImages = useQuery(imagesDoc, { variables: { color } })
  const [section, setSection] = useState<Section>("all-images")
  const loadingRef = useRef<HTMLDivElement>(null)

  const recentImageIds = arrayUniq(
    storageLocal
      .getItem("recentImages")
      ?.split(cacheDivider)
      .filter(id => id != "")
  )

  useEffect(() => {
    if (loadingRef.current) {
      loadingRef.current.scrollIntoView()
    }
  }, [uppyState.files, recentImageIds])

  if (loading) {
    return (
      <div className="image-archive">
        <div className="loading">
          <LoadingAnimation />
        </div>
      </div>
    )
  }

  if (error) {
    return <>{`Error! ${error.message}`}</>
  }

  const objects = (signState ? signState.objects : []).filter(o => Sign.isImage(o))
  const images = data?.uploadedImages || []
  const files: Array<UploadedUppyFile<any, any> | FailedUppyFile<any, any>> = []

  for (const key in uppyState.files) {
    if (Object.prototype.hasOwnProperty.call(uppyState.files, key)) {
      const file = uppyState.files[key]
      invariant(file)
      files.push(file)

      if (!file.progress?.uploadComplete) {
        setLoadingImage(true)
      }

      if (!recentImageIds?.includes(file.name)) {
        cacheImage(file.name)
      }
    }
  }

  const usedArchiveImages: Image[] = []
  allImages.data?.imageTags.forEach(imageTag => {
    imageTag.images.forEach(image => {
      if (recentImageIds?.includes(image.id)) {
        usedArchiveImages.push(image)
      }
    })
  })

  if (section == "recently-used-page" && recentImageIds) {
    return (
      <>
        <div className="top-bar">
          <span className="back" onClick={() => setSection("all-images")}>
            <ChevronLeftLarge />
            <span className="paragraph paragraph-xs bold mb-0">{t("toolbox.images")}</span>
          </span>
          <span className="paragraph bold center mb-0">{t("toolbox.recently_used")}</span>
          <span className="filler"></span>
        </div>
        <Scrollable key={images.length}>
          <div className="recently-used-page grid grid-cols-4 gap-2 p-4">
            <RecentImages
              recentImageIds={recentImageIds}
              uppy={uppy}
              usedArchiveImages={usedArchiveImages}
              files={files}
              images={images}
              handleAddImage={() => {
                setLoadingImage(true)
                setTab("active-image")
                setSection("all-images")
              }}
            />
          </div>
        </Scrollable>
      </>
    )
  }

  const lastInvalidFileIndex = invalidFiles.length - 1

  return (
    <Scrollable>
      <div className="all-objects">
        <div className="objects">
          <p className="paragraph-sm bold mb-2">{t("toolbox.add_image")}</p>
          <div className="section-buttons flex flex-col">
            <button
              className="image-archive-button button button-secondary full-width mb-2"
              onClick={e => {
                setTab("new-image")
                e.stopPropagation()
                sign.setObjectActive(null)
              }}
              type="button"
            >
              <ArchiveFill />
              {t("toolbox.browse_archive")}
            </button>
            <div className="uppy-container">
              <div className="image-upload-button button button-secondary full-width mb-0 ml-0 p-0">
                <FileInput
                  uppy={uppy}
                  inputName="files[]"
                  locale={{
                    strings: {
                      chooseFiles: t("toolbox.upload_image"),
                    },
                  }}
                />
              </div>
            </div>
            <div className="upload-list">
              {invalidFiles[lastInvalidFileIndex] && (
                <span>
                  <div className="paragraph-2xs mb-0 mt-2 font-semibold text-red-500">
                    {invalidFiles[lastInvalidFileIndex].name}
                  </div>
                  <div className="paragraph-2xs mb-0 text-red-500">{invalidFiles[lastInvalidFileIndex].error}</div>
                </span>
              )}
            </div>
          </div>

          {(objects.length > 0 || loadingPlaceholder) && (
            <>
              <div className="divider mt-4"></div>
              <p className="paragraph-sm bold mb-2">{t("toolbox.currently_in_use")}</p>
              <div className="image-objects grid gap-2">
                {objects.map((obj, i) => {
                  if (signState.doubleSided && obj.side !== signState.side) {
                    return null
                  }

                  return (
                    <div
                      key={i}
                      className="object-button flex cursor-pointer flex-col items-center rounded border-1 border-white-20 px-2 py-3"
                      onClick={() => {
                        sign.setObjectActive(obj)
                      }}
                    >
                      <ImageIcon obj={obj as SignImageState} sign={sign} />
                      <a className="underline">{t("toolbox.edit_image")}</a>
                      {obj.maxWidth && obj.maxWidth > 0 && obj.maxHeight && obj.maxHeight > 0 && (
                        <span className="paragraph-2xs center mb-0">
                          {t("toolbox.max_width")}: {obj.maxWidth} mm
                          <br />
                          {t("toolbox.max_height")}: {obj.maxHeight} mm
                        </span>
                      )}
                    </div>
                  )
                })}
                {loadingPlaceholder && (
                  <div
                    className="object-button flex cursor-pointer flex-col items-center rounded border-1 border-white-20 px-2 py-3"
                    ref={loadingRef}
                  >
                    <div className="loading mb-1">
                      <LoadingAnimation />
                    </div>
                    <a className="underline">{t("toolbox.edit_image")}</a>
                  </div>
                )}
              </div>
            </>
          )}

          {recentImageIds && recentImageIds.length > 0 && (
            <>
              <div className="divider mt-4"></div>
              <div className="header flex justify-between">
                <p className="paragraph-sm bold mb-0">{t("toolbox.recently_used")}</p>
                {recentImageIds.length > 5 && (
                  <a
                    onClick={() => {
                      setSection("recently-used-page")
                    }}
                    className="flex"
                  >
                    <span className="whitespace-nowrap">{t("toolbox.show_more")}</span>
                    <ArrowRight width={24} height={24} />
                  </a>
                )}
              </div>
              <div className="recently-used-row -ml-4">
                <Scrollable horizontal>
                  <div className="ml-4 flex pt-2">
                    <RecentImages
                      recentImageIds={recentImageIds.slice(0, rowImagesCount)}
                      uppy={uppy}
                      usedArchiveImages={usedArchiveImages}
                      files={files}
                      images={images}
                      handleAddImage={() => {
                        setLoadingImage(true)
                        setTab("active-image")
                        setSection("all-images")
                      }}
                    />
                  </div>
                </Scrollable>
              </div>
            </>
          )}
        </div>
      </div>
    </Scrollable>
  )
}

interface UploadedImageProps {
  uppy: Uppy
  file?: UploadedUppyFile<any, any> | FailedUppyFile<any, any>
  img?: Image
  archiveImg?: Image
  handleAddImage: () => void
}

const UploadedImage: React.FC<UploadedImageProps> = ({ uppy, file, img, archiveImg, handleAddImage }) => {
  const sign = useSign()
  const state = useSignState(sign)
  const [image, setImage] = useState(img || uppyFileMap[file?.id || ""])
  const wrapperRef = useRef<HTMLDivElement>(null)

  const [uploadImage] = useMutation(imageDirectUploadMutation)

  const clearFromCache = useCallback(async (id: string) => {
    const oldArray = storageLocal.getItem("recentImages")
    if (oldArray) {
      const newArray = oldArray.replace(id, "").replace(`${cacheDivider}${cacheDivider}`, cacheDivider)
      storageLocal.setItem("recentImages", newArray)
    }
  }, [])

  const [deleteMutation, deleteMutationResult] = useMutation(imageDeleteMutation, {
    update: (store, { data: imageDeleteData }) => {
      if (image && imageDeleteData?.imageDelete?.success) {
        const data = store.readQuery<UploadedImagesQuery, UploadedImagesQueryVariables>({
          query: uploadedImagesDoc,
        })

        if (data) {
          const uploadedImages = data.uploadedImages.filter(i => i.id !== image.id)

          store.writeQuery<UploadedImagesQuery, UploadedImagesQueryVariables>({
            query: uploadedImagesDoc,
            data: { ...data, uploadedImages },
          })
        }
      }
    },
  })

  const deleteImage = useCallback(async () => {
    if (image) {
      if (image.filename) {
        clearFromCache(image.filename)
      }
      await deleteMutation({ variables: { id: parseInt(image.id, 0) } })
    }

    if (file) {
      uppy.removeFile(file.id)
      clearFromCache(file.name)
    }
  }, [deleteMutation, file, image, uppy, clearFromCache])

  useEffect(() => {
    const success = (uploaded: UppyFile<Record<string, unknown>, Record<string, unknown>>, response: any) => {
      if (!file || file.id !== uploaded.id) {
        return
      }

      const uploadedFileData = {
        id: response.uploadURL || response.body?.uploadURL,
        storage: "cache",
        metadata: {
          size: uploaded.size,
          filename: uploaded.name,
          mime_type: uploaded.type,
        },
      }

      uploadImage({
        variables: { filename: uploaded.name, fileData: uploadedFileData },
        update: (store, { data }) => {
          if (data?.imageDirectUpload?.success) {
            const image = data.imageDirectUpload.image

            if (image) {
              uppyFileMap[file.id] = image
              setImage(image)

              if (sign && state) {
                addImage(
                  sign,
                  image,
                  state.multiColor,
                  handleAddImage,
                  image.description || undefined,
                  image.maxHeight,
                  image.maxWidth
                )
              }
            }
          }
        },
      })
    }

    uppy.on("upload-success", success)

    return () => {
      uppy.off("upload-success", success)
    }
  }, [uppy, file, uploadImage, handleAddImage, image, sign, state])

  if (!sign || !state) {
    return null
  }

  if (archiveImg) {
    return (
      <div
        id={`upload-${archiveImg.id}`}
        ref={wrapperRef}
        className="img-item-wrapper"
        onClick={() =>
          addImage(sign, archiveImg, archiveImg.color, handleAddImage, archiveImg.description || undefined)
        }
      >
        <div
          className="remove-img absolute hidden cursor-pointer rounded border-1 border-red-500 bg-red-100"
          onClick={e => {
            e.stopPropagation()
            clearFromCache(archiveImg.id)
            wrapperRef.current?.classList.add("hidden")
          }}
        >
          <CloseSmall />
        </div>
        <AsyncImage src={imageURL(archiveImg, state.multiColor)} loadingElement={() => <LoadingAnimation />} />
      </div>
    )
  }

  if (image) {
    return (
      <div
        id={`upload-${image.id}`}
        className={cn("img-item-wrapper", { loading: deleteMutationResult.loading })}
        onClick={() => addImage(sign, image, state.multiColor, handleAddImage, image.description || undefined)}
      >
        <div
          className="remove-img absolute hidden cursor-pointer rounded border-1 border-red-500 bg-red-100"
          onClick={e => {
            e.stopPropagation()
            deleteImage()
          }}
        >
          <CloseSmall />
        </div>
        <AsyncImage src={imageURL(image, state.multiColor)} loadingElement={() => <LoadingAnimation />} />
      </div>
    )
  }

  return null
}

interface TopBarProps {
  backTo: Tab
  header: string
  backTranslation?: string
  setActiveTab: (backTo: Tab) => void
  setToolbox: typeof actions.setToolbox
  sign: Sign
}

const TopBar: React.FC<TopBarProps> = ({ backTo, header, backTranslation, sign, setActiveTab, setToolbox }) => {
  const { t } = useTranslation()

  return (
    <div className="top-bar">
      <span
        className="back"
        onClick={() => {
          setActiveTab(backTo)

          setToolbox(null, 1)
          if (backTo == "all-objects") {
            sign.setObjectActive(null)
          }
        }}
      >
        <ChevronLeftLarge />
        <span className="paragraph paragraph-xs bold mb-0">
          {backTranslation ? backTranslation : t("toolbox.back")}
        </span>
      </span>
      <span className="paragraph bold center mb-0">{header}</span>
      <span className="filler"></span>
    </div>
  )
}

const renderHint = (signType: string) => {
  const showHint = ["plastic_sign", "vinyl_text", "name_sign"].includes(signType)
  if (!showHint) return <></>

  const hintKey =
    signType === "plastic_sign"
      ? "hint_color_images_in_alu_and_plastic"
      : signType === "vinyl_text"
        ? "hint_color_images_in_decal"
        : "hint_color_images_in_color_badge"
  const hintTarget = signType === "plastic_sign" ? "aluminium_sign" : signType === "vinyl_text" ? "sticker" : ""

  if (signType === "name_sign") {
    return <Hint show={showHint} type="tip" translation={`toolbox.${hintKey}`} />
  }

  return (
    <Hint
      show={showHint}
      type="tip"
      translation={`toolbox.${hintKey}`}
      components={{ a: <SignTypeLink type={hintTarget || ""}>alu sign</SignTypeLink> }}
    />
  )
}

interface ImageToolboxStateProps {
  data: SignData | null
  signState: SignState | null
  activeObject: SignObjectState | null
  signType: string
  subToolbox: string | null
}

interface ImageToolboxDispatchProps {
  setSubToolbox: typeof actions.setSubToolbox
  setSubTarget: typeof actions.setSubTarget
  setToolbox: typeof actions.setToolbox
}

type ImageToolboxProps = ImageToolboxStateProps & ImageToolboxDispatchProps & ToolboxStripItemProps

const ImageToolbox: React.FC<ImageToolboxProps> = ({
  sign,
  data,
  signState,
  activeObject,
  signType,
  setSubTarget,
  setToolbox,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>(
    activeObject && Sign.isImage(activeObject) ? "active-image" : "all-objects"
  )
  const [loadingPlaceholder, setLoadingPlaceholder] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    const updateTab = (state: SignObjectState | null) => {
      if (state && Sign.isImage(state)) {
        setActiveTab(prevTab => (["all-objects", "new-image"].includes(prevTab) ? "active-image" : prevTab))
      }
    }

    sign.onActiveChanged.subscribe(updateTab)

    return () => {
      sign.onActiveChanged.unsubscribe(updateTab)
    }
  }, [sign])

  useEffect(() => {
    if (activeTab === "active-image" && activeObject) {
      setLoadingPlaceholder(false)
    }
  }, [activeObject, activeTab])

  const updateObject = (state: Partial<SignImageState>) => {
    sign.updateObjectState(state)
  }

  const updateSize = (height: number) => {
    if (activeObject) {
      const width = height / activeObject.ratio
      updateObject({ height })
      updateObject({ width })
    }
  }

  const toggleMirrored = () => {
    if (activeObject) {
      updateObject({ mirrored: !activeObject.mirrored })
    }
  }

  const header = {
    "all-objects": "",
    "new-image": "",
    "active-image": "",
    "image-size": t("toolbox.size"),
    alignments: t("toolbox.alignment"),
    border: t("toolbox.border"),
    color: t("toolbox.color"),
  }

  function addNewImageTab() {
    if (!data || !signState) {
      return
    }

    let activeImage = activeObject && Sign.isImage(activeObject)

    if (activeImage && signState.doubleSided && activeObject && activeObject.side !== signState.side) {
      activeImage = false
    }

    if (activeImage) {
      return
    }

    return (
      <div className="image-archive-wrapper h-full">
        <ImageArchive
          color={data.multicolor}
          handleAddImage={id => {
            cacheImage(id)
            setLoadingPlaceholder(true)
            setActiveTab("active-image")
          }}
          handleBack={() => {
            setActiveTab("all-objects")
            sign.setObjectActive(null)
          }}
          hint={renderHint(signType)}
        />
      </div>
    )
  }

  function allObjectsTab() {
    if (!data || !signState) {
      return
    }

    return (
      <AllObjectsTab
        sign={sign}
        signState={signState}
        setTab={(tab: Tab) => setActiveTab(tab)}
        loadingPlaceholder={loadingPlaceholder}
        setLoadingImage={(value: boolean) => setLoadingPlaceholder(value)}
        color={data.multicolor}
      />
    )
  }

  function activeImageTab() {
    if (!activeObject || !data) {
      return null
    }

    const objects = (signState ? signState.objects : []).filter(o => Sign.isImage(o))
    const index = objects.indexOf(activeObject as SignImageState)

    return (
      <>
        <TopBar
          backTo="all-objects"
          header={`${t("toolbox.image")} ${index + 1}`}
          backTranslation={t("toolbox.images")}
          sign={sign}
          setActiveTab={setActiveTab}
          setToolbox={setToolbox}
        />
        <Scrollable>
          <div className="object-settings-content">
            <div className="object-option-row image-size" onClick={() => setActiveTab("image-size")}>
              <span className="left paragraph paragraph-sm bold mb-0">{t("toolbox.size")}</span>
              <span className="right">
                <span className="paragraph paragraph-sm image-size mb-0">
                  {formatSize(activeObject.width, activeObject.height)}
                </span>
                <span className="chevron">
                  <ChevronRightLarge />
                </span>
              </span>
            </div>
            <div className="object-option-row alignment" onClick={() => setActiveTab("alignments")}>
              <span className="left paragraph paragraph-sm bold mb-0">{t("toolbox.alignment")}</span>
              <span className="right">
                <span className="chevron">
                  <ChevronRightLarge />
                </span>
              </span>
            </div>
            {(data.multicolor || data.transparent) && !activeObject.colored && (
              <div className="object-option-row color" onClick={() => setActiveTab("color")}>
                <span className="left paragraph paragraph-sm bold mb-0">{t("toolbox.color")}</span>
                <span className="right">
                  <span className="paragraph paragraph-sm mb-0">
                    {t(`background_colors.${activeObject.color}`, { defaultValue: activeObject.color })}
                  </span>
                  <span
                    className={cn("color-swatch", ColorUtil.colorClass(activeObject.color))}
                    style={{ backgroundColor: activeObject.color }}
                  ></span>
                  <span className="chevron">
                    <ChevronRightLarge />
                  </span>
                </span>
              </div>
            )}
            {data.object_borders && (
              <div className="object-option-row border-type" onClick={() => setActiveTab("border")}>
                <span className="left paragraph paragraph-sm bold mb-0">{t("toolbox.border")}</span>
                <span className="right">
                  <span className="paragraph paragraph-sm mb-0">
                    {activeObject.border ? t(`shape_type.${activeObject.border.shape}`) : t("shape_type.none")}
                  </span>
                  <span className="border-icon">
                    {activeObject.border
                      ? React.createElement(ShapeIcons[activeObject.border.shape])
                      : React.createElement(ShapeIcons["none"])}
                  </span>
                  <span className="chevron">
                    <ChevronRightLarge />
                  </span>
                </span>
              </div>
            )}
            <div className="object-option-row">
              <span className="left paragraph paragraph-sm bold mb-0">{t("toolbox.rotate")}</span>
              <span className="right">
                <Button
                  name="rotate_left"
                  onClick={() => updateObject({ angle: activeObject.angle - 90 })}
                  classes="rotate-button"
                />
                <Button
                  name="rotate_right"
                  onClick={() => updateObject({ angle: activeObject.angle + 90 })}
                  classes="rotate-button"
                />
                <Button
                  name="mirrored"
                  active={activeObject.mirrored}
                  onClick={() => toggleMirrored()}
                  classes="rotate-button"
                />
              </span>
            </div>
          </div>
          <div className="actions">
            <div className="paragraph paragraph-sm bold mb-0">{t("toolbox.actions")}</div>
            <span className="buttons">
              <button
                className="reset button button-secondary mb-0"
                onClick={() => {
                  sign.resetObjectState()
                }}
                type="button"
              >
                <Reset />
                {t("toolbox.reset")}
              </button>
              <button
                className="duplicate button button-secondary mb-0"
                onClick={() => {
                  sign.duplicateObject(activeObject, sign.centerX, sign.centerY)
                }}
                type="button"
              >
                <DuplicateOutline />
                {t("misc.duplicate")}
              </button>
              <button
                className="delete button button-secondary button-error mb-0"
                onClick={e => {
                  e.stopPropagation()
                  sign.deleteObject(activeObject)
                }}
                type="button"
              >
                <TrashOutline />
                {t("misc.delete")}
              </button>
            </span>
          </div>
        </Scrollable>
      </>
    )
  }

  function imageSizeTab() {
    if (!activeObject || !signState) {
      return
    }

    const height = activeObject.height

    return (
      <div className="image-size-tab">
        <div className="object-size">
          <Slider
            rangeMinIcon={ImageSmallIcon}
            rangeMaxIcon={ImageLargeIcon}
            hideInput
            min={1}
            max={signState.heightPx * 2}
            value={height}
            onChange={value => updateSize(value)}
          />
        </div>
      </div>
    )
  }

  function alignementTab() {
    if (!activeObject) {
      return
    }

    return (
      <div className="object-alignements">
        <div className="center-buttons">
          <Button
            name="arrange_front"
            onClick={() => sign.arrangeObject(activeObject, "front")}
            label={t("toolbox.arrange_front")}
            classes="alignement-button"
          />
          <Button
            name="arrange_forward"
            onClick={() => sign.arrangeObject(activeObject, "forward")}
            label={t("toolbox.arrange_forward")}
            classes="alignement-button"
          />
          <Button
            name="arrange_backward"
            onClick={() => sign.arrangeObject(activeObject, "backward")}
            label={t("toolbox.arrange_backward")}
            classes="alignement-button"
          />
        </div>
        <div className="center-buttons">
          <Button
            name="align_center"
            active={activeObject.top === 0 && activeObject.left === 0}
            onClick={() => updateObject({ top: 0, left: 0 })}
            label={t("toolbox.align_center")}
            classes="alignement-button"
          />
          <Button
            name="align_vertical"
            active={activeObject.left !== 0 && activeObject.top === 0}
            onClick={() => updateObject({ top: 0 })}
            label={t("toolbox.align_vertical")}
            classes="alignement-button"
          />
          <Button
            name="align_horizontal"
            active={activeObject.left === 0 && activeObject.top !== 0}
            onClick={() => updateObject({ left: 0 })}
            label={t("toolbox.align_horizontal")}
            classes="alignement-button"
          />
        </div>
      </div>
    )
  }

  function borderTab() {
    return (
      <>
        {window.innerWidth <= 1023 ? (
          <div className="object-border-picker">
            <ObjectBorderPicker sign={sign} />
          </div>
        ) : (
          <Scrollable>
            <div className="object-border-picker">
              <ObjectBorderPicker sign={sign} />
            </div>
          </Scrollable>
        )}
      </>
    )
  }

  function colorTab() {
    setSubTarget("object")
    return (
      <div className="object-color-picker">
        <ColorPicker sign={sign} />
      </div>
    )
  }

  const tabComponents = () => {
    if (activeTab === "new-image") {
      return addNewImageTab()
    } else if (activeTab === "all-objects" || !activeObject) {
      return allObjectsTab()
    } else if (activeTab === "active-image") {
      return activeImageTab()
    } else if (activeTab === "image-size") {
      return imageSizeTab()
    } else if (activeTab === "alignments") {
      return alignementTab()
    } else if (activeTab === "border") {
      return borderTab()
    } else if (activeTab === "color") {
      return colorTab()
    }

    return allObjectsTab()
  }

  const fullScreen = activeTab == "new-image"

  return (
    <Toolbox className={cn("toolbox-image border-0", activeTab)}>
      <div className={cn("object-toolbox image h-full", { "full-screen": fullScreen })}>
        <div className="object-settings">
          {["image-size", "alignments", "border", "color"].includes(activeTab) && (
            <TopBar
              backTo="active-image"
              header={header[activeTab]}
              sign={sign}
              setActiveTab={setActiveTab}
              setToolbox={setToolbox}
            />
          )}
          {tabComponents()}
        </div>
      </div>
    </Toolbox>
  )
}

export default connect(
  ({ signType, signState, currentSignData, activeObject, subToolbox }: State): ImageToolboxStateProps => ({
    signType,
    signState,
    activeObject,
    data: currentSignData,
    subToolbox,
  }),
  {
    setSubToolbox: actions.setSubToolbox,
    setSubTarget: actions.setSubTarget,
    setToolbox: actions.setToolbox,
  }
)(ImageToolbox)
